import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaClipboardList, FaDownload, FaFileExcel, FaFilePdf, FaWallet, FaChartBar } from "react-icons/fa";
import AdminNavbar from "../Layout/AdminNavbar";
import ManagerNavbar from "../Layout/ManagerNavbar";
import EmployeeNavbar from "../Layout/EmployeeNavbar";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import API_BASE_URL, { COMPANY_NAME } from "../config";

const SearchableSelect = ({ options, value, onChange, placeholder = "-- Select --", searchPlaceholder = "Search...", disabled = false }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const wrapRef = useRef(null);

    const selected = options.find(o => String(o.value) === String(value));
    const filtered = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));

    useEffect(() => {
        const handleOutside = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
                setSearch("");
            }
        };
        document.addEventListener("mousedown", handleOutside);
        return () => document.removeEventListener("mousedown", handleOutside);
    }, []);

    return (
        <div ref={wrapRef} style={{ position: "relative" }}>
            <div
                className="form-select d-flex justify-content-between align-items-center"
                style={{
                    cursor: disabled ? "not-allowed" : "pointer",
                    backgroundColor: disabled ? "#e9ecef" : "#fff",
                    color: disabled ? "#6c757d" : "#212529",
                    userSelect: "none",
                }}
                onClick={() => { if (!disabled) setOpen(prev => !prev); }}
            >
                <span>{selected ? selected.label : <span className="text-muted">{placeholder}</span>}</span>
            </div>
            {open && (
                <div style={{ position: "absolute", top: "calc(100% + 2px)", left: 0, right: 0, zIndex: 1055, background: "#fff", border: "1px solid #ced4da", borderRadius: "6px", boxShadow: "0 6px 16px rgba(0,0,0,0.12)" }}>
                    <div style={{ padding: "8px 8px 4px" }}>
                        <input
                            autoFocus
                            type="text"
                            className="form-control form-control-sm"
                            placeholder={searchPlaceholder}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            onClick={e => e.stopPropagation()}
                        />
                    </div>
                    <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                        {filtered.length > 0 ? filtered.map(o => (
                            <div
                                key={o.value}
                                style={{ padding: "8px 14px", fontSize: "14px", cursor: "pointer", backgroundColor: String(o.value) === String(value) ? "#0d6efd" : "transparent", color: String(o.value) === String(value) ? "#fff" : "#212529" }}
                                onMouseEnter={e => { if (String(o.value) !== String(value)) e.currentTarget.style.backgroundColor = "#f0f4ff"; }}
                                onMouseLeave={e => { if (String(o.value) !== String(value)) e.currentTarget.style.backgroundColor = "transparent"; }}
                                onClick={() => { onChange(String(o.value)); setOpen(false); setSearch(""); }}
                            >
                                {o.label}
                            </div>
                        )) : (
                            <div style={{ padding: "10px 14px", fontSize: "13px", color: "#6c757d" }}>No results found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const LeaveView = () => {
    const navigate = useNavigate();
    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState("");
    const [leaveLogs, setLeaveLogs] = useState([]);
    const [financialYear, setFinancialYear] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [searchTerm, setSearchTerm] = useState("");
    const [entriesPerPage, setEntriesPerPage] = useState(5);
    const [currentPage, setCurrentPage] = useState(1);
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState("");
    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const [leaveSummary, setLeaveSummary] = useState([]);
    const [financialYearFrom, setFinancialYearFrom] = useState("");
    const [financialYearTo, setFinancialYearTo] = useState("");
    const [lopDays, setLopDays] = useState(0);
    const [navbarColor, setNavbarColor] = useState("#080235");
    const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) { navigate("/"); return; }
    }, [navigate]);

    useEffect(() => {
        const token = localStorage.getItem("token");
        const userId = localStorage.getItem("userId");
        if (!token || !userId) return;
        const controller = new AbortController();
        fetch(`${API_BASE_URL}/api/appearance/${userId}`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
        })
            .then(r => r.json())
            .then(d => { if (d?.navbarColor) setNavbarColor(d.navbarColor); })
            .catch(() => {});
        return () => controller.abort();
    }, []);

    const splitFinancialYear = (fy) => fy.split(/\s*[–-]\s*/);

    const getStartOfMonth = (value) => {
        if (!value) return "";
        const [month, year] = value.trim().split(" ");
        const monthIndex = new Date(`${month} 1, ${year}`).getMonth();
        if (isNaN(monthIndex)) return "";
        return `01/${String(monthIndex + 1).padStart(2, "0")}/${year}`;
    };

    const getEndOfMonth = (value) => {
        if (!value) return "";
        const [month, year] = value.trim().split(" ");
        const monthIndex = new Date(`${month} 1, ${year}`).getMonth();
        if (isNaN(monthIndex)) return "";
        const lastDay = new Date(year, monthIndex + 1, 0).getDate();
        return `${lastDay}/${String(monthIndex + 1).padStart(2, "0")}/${year}`;
    };

    useEffect(() => {
        const token = localStorage.getItem("token");
        Promise.all([
            fetch(`${API_BASE_URL}/api/company-info`, { headers: { Authorization: "Bearer " + token } }).then(r => r.json()),
            fetch(`${API_BASE_URL}/api/employees/active`, { headers: { Authorization: "Bearer " + token } }).then(r => r.json()),
        ])
            .then(([companyData, empData]) => {
                const company = Array.isArray(companyData) ? companyData[0] : companyData;
                if (company?.financialYear) {
                    setFinancialYear(company.financialYear);
                    const parts = splitFinancialYear(company.financialYear);
                    if (parts.length === 2) {
                        setFinancialYearFrom(getStartOfMonth(parts[0]));
                        setFinancialYearTo(getEndOfMonth(parts[1]));
                    }
                }
                setEmployees(empData);
                setFilteredEmployees(empData);
                setDepartments([...new Set(empData.map(emp => emp.department).filter(Boolean))]);
            })
            .catch(err => console.error("Error loading initial data:", err))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (!selectedDepartment) { setFilteredEmployees([]); setSelectedEmployee(""); return; }
        if (selectedDepartment === "ALL") { setFilteredEmployees(employees); }
        else { setFilteredEmployees(employees.filter(emp => emp.department === selectedDepartment)); }
        setSelectedEmployee("");
    }, [selectedDepartment, employees]);

    const fetchLogs = () => {
        const token = localStorage.getItem("token");
        if (!selectedEmployee || !financialYear) { setLeaveLogs([]); return; }
        fetch(`${API_BASE_URL}/api/leave-transactions/employee/${selectedEmployee}/financial-year?financialYear=${financialYear}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(res => res.json())
            .then(data => setLeaveLogs(data))
            .catch(err => console.error("Error fetching leave transactions", err));
    };

    useEffect(() => { fetchLogs(); }, [selectedEmployee, financialYear]);

    const fetchLeaveSummary = (userId) => {
        if (!userId || userId === "ALL") { setLeaveSummary([]); return; }
        const token = localStorage.getItem("token");
        fetch(`${API_BASE_URL}/api/leave-transactions/summary/${userId}?financialYear=${financialYear}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(res => res.json())
            .then(data => setLeaveSummary(data.filter(item => Number(item.remaining) > 0)))
            .catch(err => console.error("Error fetching leave summary:", err));
    };

    useEffect(() => { fetchLeaveSummary(selectedEmployee); }, [selectedEmployee]);

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token || !selectedEmployee || !financialYear) { setLopDays(0); return; }
        fetch(`${API_BASE_URL}/api/leave-transactions/employee/${selectedEmployee}/lop-days?financialYear=${financialYear}`, {
            headers: { Authorization: "Bearer " + token },
        })
            .then(res => { if (!res.ok) throw new Error(); return res.json(); })
            .then(data => setLopDays(data || 0))
            .catch(() => setLopDays(0));
    }, [selectedEmployee, financialYear]);

    useEffect(() => { setCurrentPage(1); }, [searchTerm]);

    const filteredLogs = leaveLogs.filter(log => {
        const matchesSearch =
            log.leaveType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            log.leaveCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            log.leaveStatus?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            log.reason?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            log.processedBy?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            log.appliedBy?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === "ALL" || log.leaveStatus === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const sortedLogs = [...filteredLogs].sort((a, b) => {
        if (!sortConfig.key) return 0;
        let aVal = a[sortConfig.key] ?? "";
        let bVal = b[sortConfig.key] ?? "";
        if (sortConfig.key === "startDate" || sortConfig.key === "endDate") {
            aVal = aVal ? new Date(aVal) : new Date(0);
            bVal = bVal ? new Date(bVal) : new Date(0);
        }
        if (typeof aVal === "string") { aVal = aVal.toLowerCase(); bVal = bVal.toLowerCase(); }
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
    });

    const indexOfLast = currentPage * entriesPerPage;
    const indexOfFirst = indexOfLast - entriesPerPage;
    const paginatedLogs = sortedLogs.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.ceil(filteredLogs.length / entriesPerPage);

    const handleSort = (key) => {
        setSortConfig(prev =>
            prev.key === key
                ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
                : { key, direction: "asc" }
        );
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case "APPROVED": return "bg-success";
            case "REJECTED": return "bg-danger";
            case "PENDING": return "bg-warning text-dark";
            case "FORWARDED": return "bg-primary";
            case "CREDITED": return "bg-info text-dark";
            case "ENCASHED": return "bg-secondary";
            default: return "bg-light text-dark";
        }
    };

    const truncate = (text, max = 30) =>
        text && text.length > max ? text.substring(0, max) + "..." : text;

    const handleExport = async (format) => {
        const token = localStorage.getItem("token");
        if (!selectedEmployee) {
            if (!window.confirm("Export leave report for ALL employees?")) return;
        }
        try {
            let url = `${API_BASE_URL}/api/reports/leave-transactions?format=${format}&financialYear=${financialYear}`;
            if (selectedEmployee) url += `&employeeId=${selectedEmployee}`;
            const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            if (!response.ok) throw new Error("Export failed");
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = downloadUrl;
            let fileName = "Leave_Report_All_Employees";
            if (selectedEmployee) {
                const emp = employees.find(e => String(e.id) === String(selectedEmployee));
                fileName = `Leave_Report_${emp?.user?.name?.replace(/\s+/g, "_") || "Employee"}`;
            }
            link.download = `${fileName}_${financialYear}.${format === "pdf" ? "pdf" : "xlsx"}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
            toast.success("Report downloaded");
        } catch {
            toast.error("Export failed");
        }
    };

    const renderNavbar = () => {
        const role = localStorage.getItem("activeRole") || "";
        if (role === "ROLE_MANAGER") return <ManagerNavbar />;
        if (role === "ROLE_USER") return <EmployeeNavbar />;
        return <AdminNavbar />;
    };

    if (loading) {
        return (
            <>
                <div className="fixed-top">{renderNavbar()}</div>
                <div style={{ paddingTop: "106px", minHeight: "100vh", backgroundColor: "#f0f2f5", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary mb-3" style={{ width: "3rem", height: "3rem" }} role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <div className="text-muted fw-semibold" style={{ fontSize: "14px" }}>Loading leave data...</div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <style>{`
                .lv-scroll::-webkit-scrollbar { width: 6px; }
                .lv-scroll::-webkit-scrollbar-track { background: transparent; }
                .lv-scroll::-webkit-scrollbar-thumb { background: #ced4da; border-radius: 99px; }
                .lv-scroll::-webkit-scrollbar-thumb:hover { background: #adb5bd; }

                .lv-table-wrap::-webkit-scrollbar { height: 5px; }
                .lv-table-wrap::-webkit-scrollbar-track { background: transparent; }
                .lv-table-wrap::-webkit-scrollbar-thumb { background: #ced4da; border-radius: 99px; }
                .lv-table-wrap::-webkit-scrollbar-thumb:hover { background: #adb5bd; }
            `}</style>

            <div className="fixed-top">{renderNavbar()}</div>

            <div style={{ paddingTop: "106px", height: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#f0f2f5", overflow: "hidden" }}>
            <div className="lv-scroll container-fluid" style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>

                {/* PAGE HEADER */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h4 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                            <FaClipboardList /> Leave Summary
                        </h4>
                        <small className="text-muted">View employee leave records and balance summary</small>
                    </div>
                    <div className="dropdown">
                        <button className="btn btn-success btn-sm dropdown-toggle px-3" type="button" data-bs-toggle="dropdown">
                            <FaDownload className="me-1" /> Export Report
                        </button>
                        <ul className="dropdown-menu dropdown-menu-end shadow-sm">
                            <li>
                                <button className="dropdown-item" onClick={() => handleExport("excel")}>
                                    <FaFileExcel className="me-2 text-success" /> Export Excel
                                </button>
                            </li>
                            <li>
                                <button className="dropdown-item" onClick={() => handleExport("pdf")}>
                                    <FaFilePdf className="me-2 text-danger" /> Export PDF
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* FILTER CARD */}
                <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "12px" }}>
                    <div className="card-header bg-white border-0 pt-3 pb-0 px-4">
                        <span className="fw-semibold text-secondary small">🔍 Filter</span>
                    </div>
                    <div className="card-body px-4 pb-3 pt-2">
                        <div className="row g-3 align-items-end">
                            <div className="col-md-2">
                                <label className="form-label fw-semibold small text-secondary mb-1">Department</label>
                                <SearchableSelect
                                    options={[
                                        { value: "ALL", label: "ALL Departments" },
                                        ...departments.map(dep => ({ value: dep, label: dep }))
                                    ]}
                                    value={selectedDepartment}
                                    onChange={setSelectedDepartment}
                                    placeholder="Select Department"
                                    searchPlaceholder="Search department..."
                                />
                            </div>
                            <div className="col-md-2">
                                <label className="form-label fw-semibold small text-secondary mb-1">Employee</label>
                                <SearchableSelect
                                    options={filteredEmployees.map(emp => ({ value: String(emp.id), label: emp.user.name }))}
                                    value={selectedEmployee}
                                    onChange={setSelectedEmployee}
                                    placeholder={selectedDepartment ? "Select Employee" : "Select Dept First"}
                                    searchPlaceholder="Search employee..."
                                    disabled={!selectedDepartment}
                                />
                            </div>
                            <div className="col-md-2">
                                <label className="form-label fw-semibold small text-secondary mb-1">FY From</label>
                                <input className="form-control form-control-sm bg-light text-muted" value={financialYearFrom} disabled />
                            </div>
                            <div className="col-md-2">
                                <label className="form-label fw-semibold small text-secondary mb-1">FY To</label>
                                <input className="form-control form-control-sm bg-light text-muted" value={financialYearTo} disabled />
                            </div>
                            <div className="col-md-4 d-flex gap-2 justify-content-end">
                                <button className="btn btn-danger btn-sm px-3" onClick={() => navigate(-1)}>
                                    ⏻ Exit
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* TABLE CONTROLS */}
                <div className="d-flex align-items-center justify-content-between mb-3 px-1 flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                        <div className="d-flex align-items-center gap-2">
                            <span className="text-secondary fw-semibold small">Status:</span>
                            <select
                                className="form-select form-select-sm"
                                style={{ width: "145px" }}
                                value={statusFilter}
                                onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                            >
                                <option value="ALL">All Status</option>
                                <option value="PENDING">Pending</option>
                                <option value="FORWARDED">Forwarded</option>
                                <option value="APPROVED">Approved</option>
                                <option value="REJECTED">Rejected</option>
                                <option value="CREDITED">Credited</option>
                                <option value="ENCASHED">Encashed</option>
                            </select>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <span className="text-secondary fw-semibold small">Show:</span>
                            <select
                                className="form-select form-select-sm w-auto"
                                value={entriesPerPage}
                                onChange={e => { setEntriesPerPage(Number(e.target.value)); setCurrentPage(1); }}
                            >
                                <option value={5}>5</option>
                                <option value={7}>7</option>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                            </select>
                            <span className="text-secondary small">entries</span>
                        </div>
                    </div>
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        style={{ width: "250px" }}
                        placeholder="🔍  Search leave type, status, reason..."
                        value={searchTerm}
                        onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    />
                </div>

                {/* MAIN CONTENT */}
                <div className="row g-3">
                    {/* LEFT TABLE */}
                    <div className="col-md-9">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: "12px", overflow: "hidden" }}>
                            <div className="table-responsive lv-table-wrap">
                                <table className="table table-hover table-bordered mb-0" style={{ fontSize: "13px" }}>
                                    <thead style={{ backgroundColor: navbarColor, color: "white" }}>
                                        <tr>
                                            <th onClick={() => handleSort("leaveCode")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                                                Leave Code {sortConfig.key === "leaveCode" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
                                            </th>
                                            <th onClick={() => handleSort("leaveType")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                                                Leave Type {sortConfig.key === "leaveType" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
                                            </th>
                                            <th onClick={() => handleSort("startDate")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                                                Start Date {sortConfig.key === "startDate" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
                                            </th>
                                            <th onClick={() => handleSort("endDate")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                                                End Date {sortConfig.key === "endDate" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
                                            </th>
                                            <th onClick={() => handleSort("days")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                                                Days {sortConfig.key === "days" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
                                            </th>
                                            <th onClick={() => handleSort("leaveStatus")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                                                Status {sortConfig.key === "leaveStatus" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
                                            </th>
                                            <th style={{ whiteSpace: "nowrap" }}>Leave Reason</th>
                                            <th onClick={() => handleSort("appliedBy")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                                                Applied By {sortConfig.key === "appliedBy" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
                                            </th>
                                            <th style={{ whiteSpace: "nowrap" }}>Action Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedLogs.length > 0 ? (
                                            paginatedLogs.map(log => (
                                                <tr key={log.id} style={{ verticalAlign: "middle" }}>
                                                    <td className="fw-semibold text-primary">{log.leaveCode}</td>
                                                    <td>{log.leaveType}</td>
                                                    <td className="text-nowrap">{log.startDate || "—"}</td>
                                                    <td className="text-nowrap">{log.endDate || "—"}</td>
                                                    <td className="text-center fw-bold">{log.days}</td>
                                                    <td>
                                                        <span className={`badge rounded-pill ${getStatusBadgeClass(log.leaveStatus)}`} style={{ fontSize: "11px", padding: "4px 10px" }}>
                                                            {log.leaveStatus}
                                                        </span>
                                                    </td>
                                                    <td title={log.reason || ""} style={{ maxWidth: "140px" }}>
                                                        <span className="text-muted">{truncate(log.reason, 25) || "—"}</span>
                                                    </td>
                                                    <td style={{ whiteSpace: "nowrap" }}>{log.processedBy || log.appliedBy || "—"}</td>
                                                    <td title={log.actionReason || ""} style={{ maxWidth: "120px" }}>
                                                        <span className="text-muted">{truncate(log.actionReason, 20) || "—"}</span>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="9" className="text-center py-5">
                                                    <div className="text-muted">
                                                        <div style={{ fontSize: "40px" }}>📂</div>
                                                        <div className="fw-semibold mt-2">No leave records found</div>
                                                        <small>Select a department and employee to view leave transactions</small>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* TABLE FOOTER */}
                            <div className="card-footer bg-white border-top d-flex justify-content-between align-items-center py-2 px-3" style={{ fontSize: "12px" }}>
                                <span className="text-muted">
                                    {filteredLogs.length > 0
                                        ? `Showing ${indexOfFirst + 1}–${Math.min(indexOfLast, filteredLogs.length)} of ${filteredLogs.length} records`
                                        : "No records"}
                                </span>
                                {totalPages > 1 && (
                                    <nav>
                                        <ul className="pagination pagination-sm mb-0">
                                            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                                <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>‹ Prev</button>
                                            </li>
                                            {Array.from({ length: totalPages }, (_, i) => (
                                                <li key={i} className={`page-item ${currentPage === i + 1 ? "active" : ""}`}>
                                                    <button className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                                                </li>
                                            ))}
                                            <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                                <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>Next ›</button>
                                            </li>
                                        </ul>
                                    </nav>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* RIGHT BALANCE PANEL */}
                    <div className="col-md-3">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: "12px", overflow: "hidden", position: "sticky", top: "0" }}>
                            <div className="card-header border-0 py-2 px-3" style={{ backgroundColor: navbarColor }}>
                                <h6 className="text-white mb-0 fw-semibold d-flex align-items-center gap-2">
                                    <FaWallet /> Leave Balance
                                </h6>
                            </div>
                            <div className="card-body p-0">
                                {leaveSummary.length > 0 ? (
                                    <ul className="list-group list-group-flush">
                                        {leaveSummary.map((item, index) => (
                                            <li key={index} className="list-group-item d-flex justify-content-between align-items-center px-3 py-2" style={{ fontSize: "13px" }}>
                                                <span className="text-secondary">{item.leaveType}</span>
                                                <span className="badge bg-primary rounded-pill">{item.remaining}</span>
                                            </li>
                                        ))}
                                        <li className="list-group-item d-flex justify-content-between align-items-center px-3 py-2" style={{ fontSize: "13px" }}>
                                            <span className="text-danger fw-semibold">Loss of Pay (LOP)</span>
                                            <span className="badge bg-danger rounded-pill">{lopDays}</span>
                                        </li>
                                        <li className="list-group-item d-flex justify-content-between align-items-center px-3 py-2 bg-light" style={{ fontSize: "13px" }}>
                                            <span className="fw-bold text-dark">Total Balance</span>
                                            <span className="badge bg-success rounded-pill fw-bold">
                                                {leaveSummary.reduce((sum, item) => sum + Number(item.remaining), 0)}
                                            </span>
                                        </li>
                                    </ul>
                                ) : (
                                    <>
                                        <div className="text-center text-muted py-4 px-3" style={{ fontSize: "13px" }}>
                                            <FaChartBar size={32} className="text-muted" />
                                            <div className="mt-1 small">No leave balance available</div>
                                        </div>
                                        <ul className="list-group list-group-flush">
                                            <li className="list-group-item d-flex justify-content-between align-items-center px-3 py-2" style={{ fontSize: "13px" }}>
                                                <span className="text-danger fw-semibold">Loss of Pay (LOP)</span>
                                                <span className="badge bg-danger rounded-pill">{lopDays}</span>
                                            </li>
                                        </ul>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* FOOTER */}
                <hr className="my-4" />
                <div className="text-center text-muted small mb-3">
                    © {new Date().getFullYear()} — {COMPANY_NAME} · Balsuite System
                </div>
            </div>
            </div>
        </>
    );
};

export default LeaveView;
